import type { CSSProperties, FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FigLabel } from '@/components/generals/fig-label'
import { PageHeader } from '@/components/generals/page-header'
import { Reveal } from '@/components/generals/reveal'
import { CarbonCalculator } from '@/components/materials/carbon-calculator'
import { MaterialCompare } from '@/components/materials/material-compare'
import { MaterialRows } from '@/components/materials/material-rows'

import { MATERIALS } from '@/constants/materials'
import { STUDIO_FACTS } from '@/constants/studio'

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const MaterialsPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Materials'
                description='Timber, low-carbon concrete, recycled steel, laminated glass and reclaimed brick: where Norrvik uses each, its embodied carbon and its share of the portfolio, with a carbon calculator.'
                figure='Fig. 01 / Materials / Embodied carbon'
                shape='pavilion'
            />

            <PageHeader
                fig='01'
                title='Materials'
                detail='Embodied carbon'
                note='A1 to A3 / Norrvik working values'
                lines={['What we build', 'with, and what', 'it costs the air.']}
                intro={(
                    <p>
                        Five materials make up almost all of our structures. Each is chosen for where it does the most work for the least carbon, and every number below is the value we design with.
                    </p>
                )}
                readouts={[
                    { label: 'Materials', value: String(MATERIALS.length).padStart(2, '0') },
                    { label: 'Timber share', value: `${MATERIALS[0].share}%` },
                    { label: 'Avoided to date', value: `${STUDIO_FACTS.carbonAvoided} t CO2e` },
                    { label: 'Unit', value: 'kgCO2e per m³' }
                ]}
            />

            <section aria-labelledby='materials-title' className='pt-20 desk:pt-28'>
                <Reveal className='grid gap-8 px-gutter pb-12 desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] desk:pb-16'>
                    <div>
                        <FigLabel fig='02' title='Five materials' detail='Carbon and share' note='Bars share one scale, from 0 to 4 800 kgCO2e per m³' />

                        <h2 id='materials-title' className='display-lg mt-10'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink' style={delay(80)}>
                                    Measured by
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(170)}>
                                    the cubic metre.
                                </span>
                            </span>
                        </h2>
                    </div>

                    <p className='reveal-fade body-copy self-end text-ink/85 desk:justify-self-end' style={delay(260)}>
                        Steel and glass carry many times more carbon per cubic metre than timber or brick, which is why we use them only where nothing else can do the job.
                    </p>
                </Reveal>

                <MaterialRows />
            </section>

            <section aria-labelledby='compare-title' className='pt-20 desk:pt-28'>
                <Reveal className='px-gutter pb-10'>
                    <FigLabel fig='03' title='Compare' detail='Two materials side by side' note='Pick one material for each side' />

                    <h2 id='compare-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink' style={delay(80)}>
                                Side by side,
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={delay(170)}>
                                on one scale.
                            </span>
                        </span>
                    </h2>
                </Reveal>

                <MaterialCompare />
            </section>

            <section aria-labelledby='calculator-title' className='pb-24 pt-20 desk:pb-32 desk:pt-28'>
                <Reveal className='grid gap-8 px-gutter pb-10 desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'>
                    <div>
                        <FigLabel fig='04' title='Calculator' detail='Embodied carbon of a building' note='Typical values per m² of floor area' />

                        <h2 id='calculator-title' className='display-lg mt-10'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink' style={delay(80)}>
                                    Choose the frame,
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(170)}>
                                    count the tonnes.
                                </span>
                            </span>
                        </h2>
                    </div>

                    <p className='reveal-fade body-copy self-end text-ink/85 desk:justify-self-end' style={delay(260)}>
                        Move the slider to set the floor area and pick a structure. The totals cover materials and their manufacture, stages A1 to A3, using our typical values per square metre.
                    </p>
                </Reveal>

                <CarbonCalculator />
            </section>
        </Layout>
    )
}

export default MaterialsPage
